import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Attach auth token to every request
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('auth_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// On 401, clear token and redirect to login
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401 && !error.config.url?.includes('/auth/login')) {
      localStorage.removeItem('auth_token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export const authAPI = {
  login: (password: string) => api.post('/auth/login', { password }),
  verify: () => api.get('/auth/verify'),
};

export const accountsAPI = {
  list: () => api.get('/accounts/'),
  get: (id: number) => api.get(`/accounts/${id}`),
  create: (data: any) => api.post('/accounts/', null, { params: data }),
  getLoginUrl: (id: number) => api.get(`/accounts/${id}/login-url`),
  authenticate: (id: number, requestToken: string) =>
    api.post(`/accounts/${id}/authenticate`, null, { params: { request_token: requestToken } }),
  toggle: (id: number) => api.patch(`/accounts/${id}/toggle`),
  delete: (id: number) => api.delete(`/accounts/${id}`),
  getHoldings: (id: number) => api.get(`/holdings/${id}`),
  syncHoldings: (id: number) => api.post(`/holdings/${id}/sync`),
  getSyncedHoldings: (id: number) => api.get(`/holdings/${id}/synced`),
};

export const alpacaAccountsAPI = {
  list: () => api.get('/alpaca-accounts/'),
  get: (id: number) => api.get(`/alpaca-accounts/${id}`),
  create: (data: any) => api.post('/alpaca-accounts/', null, { params: data }),
  verify: (id: number) => api.post(`/alpaca-accounts/${id}/verify`),
  toggle: (id: number) => api.patch(`/alpaca-accounts/${id}/toggle`),
  delete: (id: number) => api.delete(`/alpaca-accounts/${id}`),
  getPositions: (id: number) => api.get(`/alpaca-accounts/${id}/positions`),
  getAccountInfo: (id: number) => api.get(`/alpaca-accounts/${id}/account-info`),
};

export const upstoxAccountsAPI = {
  list: () => api.get('/upstox-accounts/'),
  get: (id: number) => api.get(`/upstox-accounts/${id}`),
  create: (data: any) => api.post('/upstox-accounts/', null, { params: data }),
  getLoginUrl: (id: number, redirectUri: string) =>
    api.get(`/upstox-accounts/${id}/login-url`, { params: { redirect_uri: redirectUri } }),
  authenticate: (id: number, authCode: string, redirectUri: string) =>
    api.post(`/upstox-accounts/${id}/authenticate`, null, { params: { auth_code: authCode, redirect_uri: redirectUri } }),
  toggle: (id: number) => api.patch(`/upstox-accounts/${id}/toggle`),
  delete: (id: number) => api.delete(`/upstox-accounts/${id}`),
  getPositions: (id: number) => api.get(`/upstox-accounts/${id}/positions`),
  getHoldings: (id: number) => api.get(`/upstox-accounts/${id}/holdings`),
  refreshInstruments: (exchange: string = 'NSE') =>
    api.post('/upstox-accounts/instruments/refresh', null, { params: { exchange } }),
};

export const fyersAccountsAPI = {
  list: () => api.get('/fyers-accounts/'),
  get: (id: number) => api.get(`/fyers-accounts/${id}`),
  create: (data: any) => api.post('/fyers-accounts/', null, { params: data }),
  getLoginUrl: (id: number, redirectUri: string) =>
    api.get(`/fyers-accounts/${id}/login-url`, { params: { redirect_uri: redirectUri } }),
  authenticate: (id: number, authCode: string) =>
    api.post(`/fyers-accounts/${id}/authenticate`, null, { params: { auth_code: authCode } }),
  toggle: (id: number) => api.patch(`/fyers-accounts/${id}/toggle`),
  delete: (id: number) => api.delete(`/fyers-accounts/${id}`),
  getPositions: (id: number) => api.get(`/fyers-accounts/${id}/positions`),
  getHoldings: (id: number) => api.get(`/fyers-accounts/${id}/holdings`),
};

export const usersAPI = {
  list: () => api.get('/users/'),
  get: (id: number) => api.get(`/users/${id}`),
  create: (data: any) => api.post('/users/', null, { params: data }),
  toggle: (id: number) => api.patch(`/users/${id}/toggle`),
  updateConfig: (id: number, config: any) => api.patch(`/users/${id}/config`, config),
  regenerateToken: (id: number, tokenType?: string) =>
    api.post(`/users/${id}/regenerate-token`, null, { params: { token_type: tokenType || 'all' } }),
  delete: (id: number) => api.delete(`/users/${id}`),
};

export const tradesAPI = {
  list: (params?: any) => api.get('/trades/', { params }),
  get: (id: number) => api.get(`/trades/${id}`),
  refresh: (id: number) => api.post(`/trades/${id}/refresh`),
  stats: (params?: any) => api.get('/trades/stats/summary', { params }),
};

export const pendingTradesAPI = {
  list: (params?: any) => api.get('/pending-trades/', { params }),
  get: (id: number) => api.get(`/pending-trades/${id}`),
  approve: (id: number, modifiedData?: any) =>
    api.post(`/pending-trades/${id}/approve`, modifiedData),
  reject: (id: number, reason?: string) =>
    api.post(`/pending-trades/${id}/reject`, { reason }),
  delete: (id: number) => api.delete(`/pending-trades/${id}`),
  cancelAll: (userId?: number) =>
    api.post('/pending-trades/cancel-all', null, { params: userId ? { user_id: userId } : undefined }),
};

export const webhookAPI = {
  send: (data: any) => api.post('/webhook/signal', data),
  test: () => api.get('/webhook/test'),
};

export const analyticsAPI = {
  getPnl: (params?: any) => api.get('/analytics/pnl', { params }),
  getIndexComparison: (params?: any) => api.get('/analytics/index-comparison', { params }),
  getAccountPerformance: () => api.get('/analytics/account-performance'),
};

export const systemAPI = {
  getNgrokUrl: () => api.get('/system/ngrok-url'),
  health: () => api.get('/system/health'),
};

export default api;
